import type { ReactNode } from 'react'

/**
 * `#authlayer` (Prototype_v2.html) — an ID selector, not a class, because there is exactly one of
 * these per page: `min-height:100vh;padding:44px 20px 60px`, the page-level gutter every `.acard`
 * (`AuthCard.tsx`) and `.acard.wide` (`OnboardingLayer.tsx`) sits inside. Both call sites
 * (`AppRoot.tsx`'s `auth` phase, `ShellLayer.tsx`'s zero-workspace gate) rendered their layer bare
 * until now — invisible on narrow `.acard` screens, whose own substantial `pt-11 px-[34px] pb-11`
 * padding partly masks the gap, and completely bare on `.acard.wide` (onboarding), which zeroes
 * its OWN padding entirely and relies on this wrapper for all of it (`.acard.wide{padding:0}`,
 * Prototype_v2.html) — the wizard was rendering flush against the viewport edges before this.
 * 44px/60px are `process/11-TOKEN-MAP.md`'s own "above the scale's top step — layout" rows, not a
 * gap in the closed `--sp-*` scale; 20px is `--sp-9`, a real token.
 *
 * `fixed inset-0 overflow-y-auto`, not `min-h-screen` — the same fix `ShellLayer.tsx`'s own
 * `<main>` needed and got: `min-height` alone has no maximum, so a tall onboarding step (the
 * "Three things to know" list, a long region picker) grew `<body>` itself past the viewport
 * instead of scrolling inside this wrapper. Bounded the same way `Sidebar`/`main` already are,
 * this wrapper's own edges stay within the viewport on all four sides; overflow content scrolls
 * inside it alone.
 *
 * No `data-cmp` — a plain, uncatalogued layout wrapper, the same shape as `ShellChrome`'s own
 * `<main>` (`ShellLayer.tsx`), not a reusable named component; kept out of `src/components/` so
 * `data-cmp-required` (scoped there — process/01-UI-CONTRACT.md §2 rule 2) does not ask it to
 * carry a catalogue entry an ID selector never had.
 *
 * `topRight` is an optional accessory (currently: `ShellLayer`'s zero-workspace wizard gate passing
 * an `AccountMenu`, the one screen this wrapper hosts where the user is already signed in but
 * `ShellChrome`/`TopBar` haven't mounted yet, so sign-out would otherwise be unreachable) rendered
 * fixed inside this wrapper's own reserved top gutter. The plain `auth` phase (sign-in etc.) never
 * passes it — those screens are pre-login, so no account action belongs there.
 */
export function AuthPageShell({ children, topRight }: { children: ReactNode; topRight?: ReactNode }) {
  return (
    <div className="fixed inset-0 overflow-y-auto pt-[44px] px-9 pb-[60px]">
      {topRight && <div className="fixed top-0 right-0 h-[44px] flex items-center px-6 z-10">{topRight}</div>}
      {children}
    </div>
  )
}
